import * as React from 'react';
import { compose, setDisplayName, withHandlers, withState, lifecycle, withProps } from 'recompose';
import { TranslateProps } from 'util/i18n';
import { ReportSettings, ReportSettingsChangeHandler } from 'logic/reports/ReportSettings';
import { makeSentenceFragments, FragmentRenderer, renderFragment } from 'util/makeSentenceFragments';
import { Input } from 'ui/shared/Input';

export type OwnProps = TranslateProps & {
    enabled: boolean;
    useDayNum: boolean;
    onChangeUseDayNum: (b: boolean) => void;
    reportSettings: Readonly<ReportSettings>;
    onChange: ReportSettingsChangeHandler;
};

const RawRecurrenceMonthlyDayNum = ({ useDayNum, handleChangeUseDayNum, enabled, sentenceFragments, renderFragment }: OwnProps & RecomposeProps) =>
    <div>
        <label>
            <input
                type="radio"
                id="edit-report-monthly-use-daynum"
                checked={ useDayNum }
                onChange={ handleChangeUseDayNum }
                disabled={ !enabled }
            />
            { sentenceFragments.map(renderFragment) }
        </label>
    </div>
;

type RecomposeProps = {
    sentenceFragments: string[];
    renderFragment: FragmentRenderer;
    dayNum: string;
    frequency: string;
    handleChangeFrequency: React.ChangeEventHandler<HTMLInputElement>;
    handleChangeUseDayNum: React.ChangeEventHandler<HTMLInputElement>;
    handleChangeDayNum: React.ChangeEventHandler<HTMLInputElement>;
};
type _RecomposeProps = RecomposeProps & {
    _setUseDayNum: (b: boolean) => void;
    _setDayNum: (f: string) => void;
    _setFrequency: (f: string) => void;
};

const update = ({ reportSettings, onChange, useDayNum, onChangeUseDayNum, dayNum, frequency }: OwnProps & _RecomposeProps) => {
    if (useDayNum) {
        onChange(reportSettings =>
            reportSettings
                .setScheduleMonthlyDayNum(parseInt(dayNum, 10))
                .setScheduleFrequency(parseInt(frequency, 10))
        );
    }
};

export const RecurrenceMonthlyDayNum = compose<OwnProps & RecomposeProps, OwnProps>(
    setDisplayName('RecurrenceMonthly'),
    withState('frequency', '_setFrequency', ({ reportSettings }: OwnProps) =>
        reportSettings.scheduleType === 'Monthly'
            ? reportSettings.useScheduleMonthlyDayNum
                ? reportSettings.scheduleFrequency.toString()
                : '1'
            : '1',
    ),
    withState('dayNum', '_setDayNum', ({ reportSettings }: OwnProps) => reportSettings.scheduleMonthlyDayNum),
    withHandlers({
        handleChangeUseDayNum: (props: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const useDayNum = e.target.checked;
            props.onChangeUseDayNum(useDayNum);
            update({ ...props, useDayNum });
        },
        handleChangeDayNum: (props: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const useDayNum = true;
            const dayNum = e.target.value;
            props._setDayNum(dayNum);
            props.onChangeUseDayNum(useDayNum);
            update({ ...props, dayNum, useDayNum });
        },
        handleChangeFrequency: (props: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const useDayNum = true;
            const frequency = e.target.value;
            props._setFrequency(frequency);
            props.onChangeUseDayNum(useDayNum);
            update({ ...props, frequency, useDayNum });
        },
    }),
    lifecycle({
        componentWillUpdate(newProps: OwnProps & _RecomposeProps) {
            // If this component becomes enabled, need to apply the current values
            // to make sure the XML reflects the values in the UI.
            if (!this.props.enabled && newProps.enabled) {
                update(newProps);
            }
        },
    }),
    withProps(({ frequency, t, enabled, ...props }: OwnProps & RecomposeProps) => ({
        sentenceFragments: makeSentenceFragments(t('WC_REPORT_SCHEDULE_MONTHLY_DAYNUM_SENTENCE', { count: parseInt(frequency, 10) })),
        renderFragment: renderFragment({
            DAYNUM: (
                <Input
                    type="number"
                    id="edit-report-monthly-daynum"
                    value={ props.dayNum }
                    onChange={ props.handleChangeDayNum }
                    min={ 1 }
                    max={ 31 }
                    maxLength={ 2 }
                    disabled={ !enabled }
                />
            ),
            INTERVAL: (
                <Input
                    type="number"
                    id="edit-report-monthly-daynum-interval"
                    value={ frequency }
                    onChange={ props.handleChangeFrequency }
                    min={ 1 }
                    max={ 12 }
                    maxLength={ 2 }
                    disabled={ !enabled }
                />
            ),
        }),
    })),
)(RawRecurrenceMonthlyDayNum);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/schedule/runOnSchedule/recurrence/RecurrenceMonthlyDayNum.tsx